import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const colors = {
  black: '#050505',
  card: '#121212',
  gold: '#D4AF37',
  cream: '#F5F5DC',
  muted: '#A1A1AA',
  white: '#FFFFFF',
  darkBorder: '#2A2A2A',
  darkGold: '#8B7320',
  emerald: '#34D399',
  blue: '#60A5FA',
  purple: '#A78BFA',
};

const typeColors = {
  new: colors.emerald,
  existing: colors.blue,
  returning: colors.purple,
};

const typeLabels = {
  new: 'New Student',
  existing: 'Current Student',
  returning: 'Returning Student',
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: colors.black,
    padding: 40,
    fontFamily: 'Helvetica',
    color: colors.cream,
  },
  coverPage: {
    backgroundColor: colors.black,
    padding: 0,
    fontFamily: 'Helvetica',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  coverBorder: {
    border: `3px solid ${colors.gold}`,
    margin: 30,
    padding: 50,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  coverSchool: {
    fontSize: 18,
    color: colors.cream,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 4,
    letterSpacing: 3,
  },
  coverTagline: {
    fontSize: 9,
    color: colors.muted,
    textAlign: 'center',
    letterSpacing: 3,
  },
  coverLine: {
    width: 120,
    height: 2,
    backgroundColor: colors.gold,
  },
  coverTitle: {
    fontSize: 32,
    color: colors.gold,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 10,
    letterSpacing: 1,
  },
  coverName: {
    fontSize: 20,
    color: colors.cream,
    textAlign: 'center',
    marginBottom: 6,
  },
  coverType: {
    fontSize: 11,
    textAlign: 'center',
    letterSpacing: 2,
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderRadius: 10,
  },
  coverDate: {
    fontSize: 9,
    color: colors.muted,
    textAlign: 'center',
    marginTop: 30,
  },
  sectionHeader: {
    fontSize: 20,
    color: colors.gold,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 14,
    paddingBottom: 6,
    borderBottom: `2px solid ${colors.gold}`,
  },
  subsectionHeader: {
    fontSize: 14,
    color: colors.gold,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6,
    marginTop: 12,
  },
  paragraph: {
    fontSize: 10,
    color: colors.cream,
    lineHeight: 1.6,
    marginBottom: 8,
  },
  mutedText: {
    fontSize: 9,
    color: colors.muted,
    lineHeight: 1.5,
    marginBottom: 4,
  },
  bulletItem: {
    fontSize: 10,
    color: colors.cream,
    lineHeight: 1.5,
    marginBottom: 3,
    paddingLeft: 12,
  },
  bulletGold: {
    color: colors.gold,
    fontFamily: 'Helvetica-Bold',
  },
  cardBox: {
    backgroundColor: colors.card,
    borderRadius: 6,
    padding: 14,
    marginBottom: 10,
    border: `1px solid ${colors.darkBorder}`,
  },
  classCard: {
    backgroundColor: colors.card,
    borderRadius: 5,
    padding: 10,
    marginBottom: 8,
    borderLeft: `3px solid ${colors.gold}`,
  },
  className: {
    fontSize: 11,
    color: colors.cream,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  classroomLabel: {
    fontSize: 8,
    color: colors.gold,
    letterSpacing: 1,
    marginBottom: 3,
  },
  classDesc: {
    fontSize: 9,
    color: colors.muted,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 3,
  },
  tag: {
    fontSize: 7,
    color: colors.gold,
    backgroundColor: '#1a1a0a',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    border: `1px solid ${colors.darkGold}`,
  },
  recordingTag: {
    fontSize: 7,
    color: colors.blue,
    backgroundColor: '#0a0a1a',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    border: `1px solid #3a3a6a`,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  stepCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.gold,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumber: {
    fontSize: 12,
    color: colors.black,
    fontFamily: 'Helvetica-Bold',
  },
  stepContent: {
    flex: 1,
    paddingTop: 2,
  },
  stepTitle: {
    fontSize: 11,
    color: colors.cream,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  stepDesc: {
    fontSize: 9,
    color: colors.muted,
    lineHeight: 1.5,
  },
  scheduleGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },
  scheduleChip: {
    fontSize: 9,
    color: colors.cream,
    backgroundColor: '#1a1a1a',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
    border: `1px solid ${colors.darkBorder}`,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  infoLabel: {
    fontSize: 9,
    color: colors.muted,
    width: 100,
  },
  infoValue: {
    fontSize: 10,
    color: colors.cream,
    flex: 1,
  },
  pageFooter: {
    position: 'absolute',
    fontSize: 8,
    bottom: 20,
    left: 40,
    color: colors.darkGold,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 8,
    bottom: 20,
    right: 40,
    color: colors.muted,
  },
  divider: {
    height: 1,
    backgroundColor: colors.darkBorder,
    marginVertical: 12,
  },
  twoCol: {
    flexDirection: 'row',
    gap: 12,
  },
  col: {
    flex: 1,
  },
  goalBox: {
    backgroundColor: '#0a0a0a',
    borderRadius: 6,
    padding: 12,
    borderLeft: `3px solid ${colors.gold}`,
    marginBottom: 8,
  },
  goalLabel: {
    fontSize: 8,
    color: colors.gold,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 1,
    marginBottom: 4,
  },
  goalText: {
    fontSize: 10,
    color: colors.cream,
    lineHeight: 1.6,
    fontStyle: 'italic',
  },
});

const Bullet = ({ children }) => (
  <Text style={styles.bulletItem}>
    <Text style={styles.bulletGold}>{'\u2022'} </Text>{children}
  </Text>
);

const PageFooter = ({ section }) => (
  <>
    <Text style={styles.pageFooter}>Nathaniel School of Music</Text>
    <Text style={styles.pageNumber}>{section}</Text>
  </>
);

// Classroom descriptions for enriching the PDF
const classroomInsights = {
  'Music Gym': 'Intensive training modules for building musical strength, speed, and endurance through systematic exercises.',
  'Music Factory': 'Creative production hub where you learn to create, arrange, transcribe, and produce music.',
  'Modular Piano': 'Flexible approach to piano technique covering specific skills you choose - from sight reading to bass lines.',
  'Theory & Ear Training': 'Comprehensive musicianship training that changes how you hear and create music.',
  'Seasonal & Genre Based': 'Themed modules covering specific genres - gospel, blues, Bollywood, Latin, and more.',
  'New Offerings': 'Collaborative programs including jam sessions, performance workshops, and mentorship.',
  'Foundation Lessons': 'Core foundational courses ensuring proper technique, reading ability, and musical understanding.',
  'Perks & Tiers': 'Additional benefits including productivity tools, practice apps, doubt clearing, and studio access.',
};

const CoverPage = ({ formData, studentType }) => {
  const accentColor = typeColors[studentType] || colors.gold;
  const label = typeLabels[studentType] || 'Student';
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <Page size="A4" style={styles.coverPage}>
      <View style={styles.coverBorder}>
        <Text style={styles.coverSchool}>NATHANIEL SCHOOL OF MUSIC</Text>
        <Text style={styles.coverTagline}>PIANO EDUCATION, REDEFINED</Text>
        <View style={{ marginVertical: 30 }}>
          <View style={styles.coverLine} />
        </View>
        <Text style={styles.coverTitle}>Your Semester{'\n'}Selection Guide</Text>
        <Text style={styles.coverName}>Prepared for {formData.full_name}</Text>
        <View style={{ marginTop: 12 }}>
          <Text style={{ ...styles.coverType, color: accentColor, border: `1px solid ${accentColor}` }}>
            {label.toUpperCase()}
          </Text>
        </View>
        <Text style={styles.coverDate}>{today}</Text>
        <Text style={{ ...styles.coverDate, marginTop: 6 }}>
          {formData.class_interests?.length || 0} classes selected across {
            [...new Set((formData.class_interests || []).map(ci => ci.classroom_name))].length
          } classrooms
        </Text>
      </View>
    </Page>
  );
};

const WelcomePage = ({ formData, studentType }) => {
  const welcomeMessages = {
    new: `Welcome to Nathaniel School of Music, ${formData.full_name.split(' ')[0]}! We're thrilled that you've taken the first step toward your musical journey. This guide is a summary of the classes and preferences you've shared with us. Our team will review your selections and craft a personalized semester package tailored to your goals.`,
    existing: `Great to see you exploring new options, ${formData.full_name.split(' ')[0]}! Our syllabus has evolved and we're excited to help you discover classes that match where you are now. This guide summarizes your updated selections and preferences. We'll use this to redesign your semester package.`,
    returning: `Welcome back, ${formData.full_name.split(' ')[0]}! We've missed you. A lot has changed since you were last here — new classes, jam sessions, in-person workshops, and more. This guide captures what you're interested in for your comeback semester. Let's make it count.`,
  };

  return (
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionHeader}>Welcome</Text>
      <View style={styles.cardBox}>
        <Text style={styles.paragraph}>{welcomeMessages[studentType]}</Text>
      </View>

      <Text style={styles.subsectionHeader}>What Happens Next</Text>
      <View style={{ marginTop: 4 }}>
        {[
          { num: '1', title: 'We Review Your Submission', desc: 'Our team carefully reviews your class selections, goals, and schedule preferences within 24-48 hours.' },
          { num: '2', title: 'We Reach Out', desc: 'A team member will call or message you to discuss your selections, answer questions, and understand your goals more deeply.' },
          { num: '3', title: 'Your Custom Package', desc: 'Based on our conversation, we\'ll design a personalized semester package with the right mix of classes and hours for you.' },
          { num: '4', title: 'Review & Enroll', desc: 'You\'ll receive a detailed package link to review. Once you\'re happy with it, we\'ll get you started!' },
        ].map((step) => (
          <View key={step.num} style={styles.stepRow}>
            <View style={styles.stepCircle}>
              <Text style={styles.stepNumber}>{step.num}</Text>
            </View>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>{step.title}</Text>
              <Text style={styles.stepDesc}>{step.desc}</Text>
            </View>
          </View>
        ))}
      </View>

      <View style={styles.divider} />

      <Text style={styles.subsectionHeader}>Track Your Application</Text>
      <View style={styles.cardBox}>
        <Text style={styles.paragraph}>
          You can track your application status anytime at students.nathanielschool.com/track using your email address ({formData.email}). You'll see real-time updates as your application moves through our process.
        </Text>
      </View>

      <PageFooter section="Welcome" />
    </Page>
  );
};

const ClassSelectionPage = ({ formData, classrooms }) => {
  // Group selected classes by classroom
  const groupedClasses = {};
  (formData.class_interests || []).forEach(ci => {
    const key = ci.classroom_name;
    if (!groupedClasses[key]) groupedClasses[key] = [];
    groupedClasses[key].push(ci);
  });

  // Find class details from classrooms data
  const getClassDetails = (classId) => {
    if (!classrooms) return null;
    for (const cr of classrooms) {
      const cls = (cr.classes || []).find(c => c.id === classId);
      if (cls) return cls;
    }
    return null;
  };

  return (
    <Page size="A4" style={styles.page} wrap>
      <Text style={styles.sectionHeader}>
        Your Selected Classes ({formData.class_interests?.length || 0})
      </Text>
      <Text style={styles.mutedText}>
        These are the classes you've expressed interest in, organized by classroom.
        Our team will use these to build your custom semester package.
      </Text>

      {Object.entries(groupedClasses).map(([classroomName, classes]) => (
        <View key={classroomName} style={{ marginTop: 10 }} wrap={false}>
          <Text style={{ ...styles.subsectionHeader, marginTop: 0 }}>{classroomName}</Text>
          <Text style={styles.mutedText}>{classroomInsights[classroomName] || ''}</Text>
          {classes.map((ci) => {
            const details = getClassDetails(ci.class_id);
            return (
              <View key={ci.class_id} style={styles.classCard} wrap={false}>
                <Text style={styles.className}>{ci.class_name}</Text>
                {details && (
                  <>
                    <Text style={styles.classDesc}>{details.description}</Text>
                    <View style={styles.tagRow}>
                      {(details.musical_benefits || []).slice(0, 3).map((b, j) => (
                        <Text key={j} style={styles.tag}>{b}</Text>
                      ))}
                      {ci.prefer_recording && (
                        <Text style={styles.recordingTag}>HD Recording</Text>
                      )}
                    </View>
                  </>
                )}
                {!details && ci.prefer_recording && (
                  <View style={styles.tagRow}>
                    <Text style={styles.recordingTag}>HD Recording</Text>
                  </View>
                )}
              </View>
            );
          })}
        </View>
      ))}
      <PageFooter section="Class Selection" />
    </Page>
  );
};

const GoalsAndSchedulePage = ({ formData, studentType }) => {
  return (
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionHeader}>Your Goals & Preferences</Text>

      {/* Goals */}
      {formData.goals && (
        <View style={styles.goalBox}>
          <Text style={styles.goalLabel}>MUSICAL GOALS</Text>
          <Text style={styles.goalText}>"{formData.goals}"</Text>
        </View>
      )}

      {formData.songs_to_learn && (
        <View style={styles.goalBox}>
          <Text style={styles.goalLabel}>SONGS TO LEARN</Text>
          <Text style={styles.goalText}>"{formData.songs_to_learn}"</Text>
        </View>
      )}

      {formData.technical_challenges && (
        <View style={styles.goalBox}>
          <Text style={styles.goalLabel}>TECHNICAL CHALLENGES</Text>
          <Text style={styles.goalText}>"{formData.technical_challenges}"</Text>
        </View>
      )}

      {formData.new_areas_to_explore && (
        <View style={styles.goalBox}>
          <Text style={styles.goalLabel}>NEW AREAS TO EXPLORE</Text>
          <Text style={styles.goalText}>"{formData.new_areas_to_explore}"</Text>
        </View>
      )}

      <View style={styles.divider} />

      {/* Schedule */}
      <Text style={styles.subsectionHeader}>Schedule Preferences</Text>

      {formData.preferred_days?.length > 0 && (
        <View style={{ marginBottom: 10 }}>
          <Text style={styles.mutedText}>Preferred Days</Text>
          <View style={styles.scheduleGrid}>
            {formData.preferred_days.map(day => (
              <Text key={day} style={styles.scheduleChip}>{day}</Text>
            ))}
          </View>
        </View>
      )}

      {formData.preferred_times?.length > 0 && (
        <View style={{ marginBottom: 10 }}>
          <Text style={styles.mutedText}>Preferred Times</Text>
          <View style={styles.scheduleGrid}>
            {formData.preferred_times.map(time => (
              <Text key={time} style={styles.scheduleChip}>{time}</Text>
            ))}
          </View>
        </View>
      )}

      {formData.interested_in_person && (
        <View style={{ ...styles.cardBox, borderLeft: `3px solid ${colors.gold}` }}>
          <Text style={{ ...styles.className, color: colors.gold }}>In-Person Sessions</Text>
          <Text style={styles.classDesc}>
            You've expressed interest in attending in-person sessions at our Bangalore studio.
            This includes access to professional recording equipment, jam rooms, and hands-on workshops.
          </Text>
        </View>
      )}

      <View style={styles.divider} />

      {/* Student-type specific info */}
      {studentType === 'new' && formData.musical_journey && (
        <View style={styles.cardBox}>
          <Text style={styles.subsectionHeader}>Your Profile</Text>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Experience</Text>
            <Text style={styles.infoValue}>
              {formData.musical_journey === 'beginner_intermediate' ? 'Beginner - Intermediate' :
               formData.musical_journey === 'seasoned' ? 'Seasoned Musician' :
               formData.musical_journey === 'producer' ? 'Music Producer' :
               formData.musical_journey === 'educator' ? 'Music Educator' : formData.musical_journey}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Location</Text>
            <Text style={styles.infoValue}>{formData.city}, {formData.country}</Text>
          </View>
        </View>
      )}

      {studentType === 'existing' && formData.current_hour_package && (
        <View style={styles.cardBox}>
          <Text style={styles.subsectionHeader}>Current Package</Text>
          <Text style={styles.paragraph}>
            You're currently on a {formData.current_hour_package}-hour semester package. We'll review your
            new class selections and design an updated package that better fits your evolving goals.
          </Text>
        </View>
      )}

      {studentType === 'returning' && (
        <View style={styles.cardBox}>
          <Text style={styles.subsectionHeader}>Your History</Text>
          {formData.semesters_completed && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Semesters</Text>
              <Text style={styles.infoValue}>{formData.semesters_completed} completed</Text>
            </View>
          )}
          {formData.last_package_hours && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Last Package</Text>
              <Text style={styles.infoValue}>{formData.last_package_hours} hours</Text>
            </View>
          )}
          {formData.favorite_classes && (
            <>
              <Text style={{ ...styles.mutedText, marginTop: 6 }}>What you enjoyed most:</Text>
              <Text style={styles.paragraph}>{formData.favorite_classes}</Text>
            </>
          )}
        </View>
      )}

      <PageFooter section="Goals & Schedule" />
    </Page>
  );
};

const AboutNSMPage = () => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.sectionHeader}>About Nathaniel School of Music</Text>

    <View style={styles.cardBox}>
      <Text style={styles.paragraph}>
        Nathaniel School of Music was founded with a simple belief: music education should be as
        unique as the musician. Led by Jason Zac — a pianist, producer, and educator with decades
        of experience across gospel, jazz, Bollywood, and contemporary music — NSM offers a
        modular curriculum that lets you design your own learning path.
      </Text>
    </View>

    <Text style={styles.subsectionHeader}>What Makes Us Different</Text>
    <View style={styles.twoCol}>
      <View style={styles.col}>
        <View style={styles.cardBox}>
          <Text style={{ ...styles.className, color: colors.gold }}>60+ Classes</Text>
          <Text style={styles.classDesc}>
            Across 7 classrooms covering technique, theory, production, genres, and collaboration.
            You pick what matters — skip the rest.
          </Text>
        </View>
        <View style={styles.cardBox}>
          <Text style={{ ...styles.className, color: colors.gold }}>HD Recordings</Text>
          <Text style={styles.classDesc}>
            Every live session is recorded in HD with study materials.
            Can't make it live? No problem — learn on your schedule.
          </Text>
        </View>
      </View>
      <View style={styles.col}>
        <View style={styles.cardBox}>
          <Text style={{ ...styles.className, color: colors.gold }}>Learn from Anywhere</Text>
          <Text style={styles.classDesc}>
            Students from India, the US, UK, UAE, and beyond.
            All sessions are live online with global access.
          </Text>
        </View>
        <View style={styles.cardBox}>
          <Text style={{ ...styles.className, color: colors.gold }}>Bangalore Studio</Text>
          <Text style={styles.classDesc}>
            In-person workshops, jam sessions, and professional
            recording studio access for local students.
          </Text>
        </View>
      </View>
    </View>

    <Text style={styles.subsectionHeader}>The 7 Classrooms</Text>
    {Object.entries(classroomInsights).filter(([name]) => name !== 'Perks & Tiers').map(([name, desc]) => (
      <View key={name} style={{ flexDirection: 'row', marginBottom: 4 }}>
        <Text style={{ ...styles.bulletGold, fontSize: 9, width: 130 }}>{name}</Text>
        <Text style={{ fontSize: 8, color: colors.muted, flex: 1 }}>{desc}</Text>
      </View>
    ))}

    <View style={styles.divider} />

    <Text style={styles.subsectionHeader}>Get in Touch</Text>
    <View style={styles.cardBox}>
      <View style={styles.infoRow}>
        <Text style={styles.infoLabel}>Email</Text>
        <Text style={styles.infoValue}>music@nathanielschool.com</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.infoLabel}>WhatsApp</Text>
        <Text style={styles.infoValue}>+91 7760456847</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.infoLabel}>Website</Text>
        <Text style={styles.infoValue}>students.nathanielschool.com</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.infoLabel}>Track Status</Text>
        <Text style={styles.infoValue}>students.nathanielschool.com/track</Text>
      </View>
    </View>

    <PageFooter section="About NSM" />
  </Page>
);

const StudentWelcomePDF = ({ formData, studentType, classrooms }) => (
  <Document
    title={`NSM Course Selection Guide - ${formData.full_name}`}
    author="Nathaniel School of Music"
    subject="Student Course Selection Guide"
  >
    <CoverPage formData={formData} studentType={studentType} />
    <WelcomePage formData={formData} studentType={studentType} />
    <ClassSelectionPage formData={formData} classrooms={classrooms} />
    <GoalsAndSchedulePage formData={formData} studentType={studentType} />
    <AboutNSMPage />
  </Document>
);

export default StudentWelcomePDF;
